import React, { useEffect, useState } from 'react';
import { getHistoricoPagamentoApi } from '../../Api';
import ModalPix from '../../components/ProfileManager/ModalPix';

export default function HistoricoPagamentos() {
    const [historico, setHistorico] = useState([]);
    const [loading, setLoading] = useState(true);
    // mercado_pago_id do pagamento Pix que o usuário clicou em "Pagar agora".
    // Quando setado, abre o ModalPix (que consulta o pagamento e mostra o QR).
    const [pixModalId, setPixModalId] = useState(null);

    const carregar = async (signal) => {
        try {
            const response = await getHistoricoPagamentoApi({ signal });
            if (response.data.success) {
                setHistorico(response.data.data);
            }
        } catch (error) {
            if (signal?.aborted) return;
            console.error("Erro ao carregar histórico", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const controller = new AbortController();
        carregar(controller.signal);
        return () => controller.abort();
    }, []);

    // Chamado pelo ModalPix ao aprovar OU cancelar: fecha o modal e recarrega
    // o histórico (o status do pagamento mudou).
    const handlePixResolvido = () => {
        setPixModalId(null);
        carregar();
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Função para estilizar o status
    const getStatusStyle = (status) => {
        switch (status) {
            case 'approved':
                return { back: '#DEF7EC', text: '#03543F', label: 'Aprovado' };
            case 'pending':
                return { back: '#FEF3C7', text: '#92400E', label: 'Pendente' };
            case 'rejected':
                return { back: '#FDE8E8', text: '#9B1C1C', label: 'Recusado' };
            default:
                return { back: '#F3F4F6', text: '#374151', label: status };
        }
    };

    const styles = {
        container: { padding: '20px', maxWidth: '1000px', margin: '0 auto', fontFamily: 'sans-serif' },
        header: { color: '#006437', marginBottom: '25px', fontWeight: '800' },
        card: {
            backgroundColor: '#fff',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            overflow: 'hidden',
            border: '1px solid #edf2f7'
        },
        table: { width: '100%', borderCollapse: 'collapse', textAlign: 'left' },
        th: { padding: '15px', backgroundColor: '#f8fafc', color: '#64748b', fontSize: '13px', textTransform: 'uppercase' },
        td: { padding: '15px', borderTop: '1px solid #edf2f7', color: '#1e293b', fontSize: '14px' },
        badge: (status) => ({
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: 'bold',
            backgroundColor: getStatusStyle(status).back,
            color: getStatusStyle(status).text
        }),
        emptyState: { textAlign: 'center', padding: '40px', color: '#94a3b8' },
        produtoTitulo: { fontWeight: 600, color: '#0F172A', lineHeight: 1.3 },
        produtoSub: {
            fontSize: '12px', color: '#64748B', marginTop: 2,
            textTransform: 'uppercase', letterSpacing: '0.03em',
        },
        loading: {
            padding: '4rem 1rem', textAlign: 'center',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem',
        },
        loadingTxt: { color: '#64748B', margin: 0, fontSize: '0.92rem' },
        spinner: {
            width: 32, height: 32,
            border: '3px solid #E2E8F0',
            borderTopColor: '#059669',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
        },
    };

    if (loading) {
        return (
            <div style={styles.loading}>
                <div style={styles.spinner} />
                <p style={styles.loadingTxt}>Carregando histórico…</p>
                <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <h1 style={styles.header}>Histórico de Pagamentos</h1>

            <div style={styles.card}>
                {historico.length > 0 ? (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Data</th>
                                    <th style={styles.th}>Produto</th>
                                    <th style={styles.th}>Valor</th>
                                    <th style={styles.th}>Status</th>
                                    <th style={styles.th}>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {historico.map((pagamento) => (
                                    <tr key={pagamento.id}>
                                        <td style={styles.td}>{formatDate(pagamento.created_at)}</td>
                                        <td style={styles.td}>
                                            <div style={styles.produtoTitulo}>
                                                {pagamento.product_title || 'Taxa de Emissão'}
                                            </div>
                                            {(pagamento.product_card_type || pagamento.product_ano) && (
                                                <div style={styles.produtoSub}>
                                                    {[pagamento.product_card_type, pagamento.product_ano]
                                                        .filter(Boolean)
                                                        .join(' · ')}
                                                </div>
                                            )}
                                        </td>
                                        <td style={styles.td}><strong>{formatCurrency(pagamento.amount)}</strong></td>
                                        <td style={styles.td}>
                                            <span style={styles.badge(pagamento.status)}>
                                                {getStatusStyle(pagamento.status).label}
                                            </span>
                                        </td>
                                        <td style={styles.td}>
                                            {pagamento.status === 'pending' && (
                                                <button
                                                    onClick={() => setPixModalId(pagamento.mercado_pago_id)}
                                                    style={{
                                                        border: 'none',
                                                        background: 'none',
                                                        color: '#006437',
                                                        cursor: 'pointer',
                                                        fontWeight: 'bold',
                                                        textDecoration: 'underline'
                                                    }}
                                                >
                                                    Pagar agora
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div style={styles.emptyState}>
                        <p>Nenhum pagamento encontrado.</p>
                    </div>
                )}
            </div>

            {pixModalId && (
                <ModalPix
                    paymentId={pixModalId}
                    onSuccess={handlePixResolvido}
                    onClose={() => setPixModalId(null)}
                />
            )}
        </div>
    );
}